import { foodOrigin } from '../lib/food-origin';
import { MealPhotoScreen } from './MealPhoto';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PHOTO_ACCEPT, readPhotoFile } from '../lib/photo-upload';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { validGtinChecksum, type Food, type Meal } from '@jungles/shared';
import { ApiError, api, request } from '../lib/api';
import { labelPrefill, type LabelPhotoResult } from '../lib/photo-prefill';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { CloseIcon, FlashIcon } from '../components/icons';
import { createBarcodeReader, isProductBarcode, type BarcodeReader } from '../lib/barcode';
import type { ScanPrefill } from './FoodForm';

/** N2 · Сканирование штрихкода и этикетки (SPEC §5, §8). */

type Mode = 'barcode' | 'label';
type Problem = { kind: 'not_found' | 'unavailable' | 'ocr'; message: string; code?: string };

export function ScanScreen() {
  const [params] = useSearchParams();
  return params.get('mode') === 'meal' ? <MealPhotoScreen /> : <BarcodeScanScreen />;
}

function BarcodeScanScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const origin = foodOrigin(useLocation().state);
  const toast = useToast();

  const meal = (params.get('meal') ?? 'snack') as Meal;
  const date = params.get('date') ?? '';
  const query = `meal=${meal}&date=${date}`;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanning = useRef(false);
  const lookupRef = useRef<{ generation: number; controller: AbortController | null; code: string }>({ generation: 0, controller: null, code: '' });
  const invalidateLookup = useCallback(() => {
    lookupRef.current.generation++;
    lookupRef.current.controller?.abort();
    lookupRef.current.controller = null;
  }, []);
  useEffect(() => invalidateLookup, [invalidateLookup]);

  const readerRef = useRef<BarcodeReader | null>(null);
  const [engine, setEngine] = useState<'native' | 'wasm' | 'none' | 'loading'>('loading');
  const [mode, setMode] = useState<Mode>('barcode');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [choices,setChoices] = useState<{code:string;foods:(Food & {restrictedCandidate:true})[]} | null>(null);
  const choiceHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const manualToggleRef = useRef<HTMLButtonElement | null>(null);
  const returningToScan = useRef(false);
  const choosing = choices !== null;
  useEffect(() => {
    if (choosing) choiceHeadingRef.current?.focus();
    else if (returningToScan.current) {
      returningToScan.current = false;
      manualToggleRef.current?.focus();
    }
  }, [choosing]);
  const backToScan = () => {
    invalidateLookup();
    setBusy(false);
    setProblem(null);
    setManualOpen(false);
    setManualCode('');
    lookupRef.current.code = '';
    setMode('barcode');
    returningToScan.current = true;
    setChoices(null);
  };

  const stop = useCallback(() => {
    scanning.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const handleFound = useCallback(
    (food: Pick<Food, 'name' | 'brand' | 'barcode'> & { id?:string;restrictedCandidate?:boolean;source?:Food['source'];catalogOrigin?:Food['catalogOrigin'];kcal100: number | null; p100: number | null; f100: number | null; c100: number | null; notes?: string; nutritionBasis?: Food['nutritionBasis'] }, incomplete = false, code = '') => {
      if (!incomplete && food.id) {
        hapticSuccess(); stop(); invalidateLookup();
        navigate(`/nutrition/food?${query}&foodId=${encodeURIComponent(food.id)}&barcode=${encodeURIComponent(code)}`, { state: { foodOrigin: origin, food: { ...food, barcode: code || food.barcode } } });
        return;
      }
      const prefill: ScanPrefill = {
        name: food.name,
        brand: food.brand,
        grams: 100,
        basis: 'per100',
        kcal: food.kcal100,
        protein: food.p100,
        fat: food.f100,
        carbs: food.c100,
        barcode: code || food.barcode,
        foodId: food.id,
        source: 'barcode',
        barcodeOrigin: food.id || food.restrictedCandidate ? 'lookup' : 'manual',
        restrictedCandidate: food.restrictedCandidate,
        nutritionBasis: food.nutritionBasis,
        notes: food.id ? food.notes ?? (incomplete ? 'Продукт найден, но КБЖУ неполные. Введите недостающие значения вручную с упаковки.' : null) : 'Введите название и КБЖУ с упаковки.',
      };
      hapticSuccess();
      stop();
      invalidateLookup();
      navigate(`/nutrition/food?${query}`, { state: { foodOrigin: origin, prefill } });
    },
    [navigate, query, stop, invalidateLookup, origin],
  );

  const lookup = useCallback(
    async (code: string) => {
      if (lookupRef.current.controller) return;
      if (!isProductBarcode(code)) { toast('Штрихкод — это 8–14 цифр'); return; }
      if ([8, 12, 13, 14].includes(code.length) && !validGtinChecksum(code)) { toast('Проверьте контрольную цифру штрихкода'); return; }
      setChoices(null);
      lookupRef.current.code = code;
      const generation = ++lookupRef.current.generation;
      const controller = new AbortController();
      lookupRef.current.controller = controller;
      setBusy(true);
      try {
        const result = await api.get<Food | { status: 'incomplete'; food: Parameters<typeof handleFound>[0] } | {status:'ambiguous';foods:(Food & {restrictedCandidate:true})[]}>(`/api/foods/barcode/${encodeURIComponent(code)}`, controller.signal);
        if (controller.signal.aborted || generation !== lookupRef.current.generation) return;
        if ('status' in result && result.status === 'ambiguous') {stop();setChoices({code,foods:result.foods});}
        else if ('status' in result) handleFound(result.food, true, code);
        else handleFound(result, false, code);
      } catch (error) {
        if (controller.signal.aborted || generation !== lookupRef.current.generation) return;
        scanning.current = false;
        if (error instanceof ApiError && error.status === 404) {
          setProblem({ kind: 'not_found', message: error.message, code });
        } else if (error instanceof ApiError && error.status === 503) {
          setProblem({ kind: 'unavailable', message: error.message, code });
        } else {
          setProblem({
            kind: 'unavailable',
            message: 'Не удалось проверить код — попробуйте позже или добавьте вручную',
            code,
          });
        }
      } finally {
        if (generation === lookupRef.current.generation) {
          lookupRef.current.controller = null;
          setBusy(false);
        }
      }
    },
    [handleFound, toast, stop],
  );

  useEffect(() => {
    if (choosing) return;
    let cancelled = false;

    const start = async () => {
      setCameraError(null);
      try {
        // Разрешение просим высокое: на штрихкод должно прийтись достаточно
        // пикселей, иначе полосы сливаются и распознавание буксует.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        // Непрерывный автофокус — там, где он поддерживается. Без него камера
        // часто держит фокус на дальнем плане, а код остаётся размытым.
        const track = stream.getVideoTracks()[0];
        if (track) {
          const caps = track.getCapabilities?.() as
            | { focusMode?: string[] }
            | undefined;
          if (caps?.focusMode?.includes('continuous')) {
            await track
              .applyConstraints({
                advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
              })
              .catch(() => undefined);
          }
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch {
        if (!cancelled) {
          setCameraError('Камера недоступна — введите цифры штрихкода или добавьте вручную');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stop, choosing]);

  // Движок распознавания поднимаем один раз: нативный, иначе ZXing (WASM).
  useEffect(() => {
    let cancelled = false;
    void createBarcodeReader().then((reader) => {
      if (cancelled) return;
      readerRef.current = reader;
      setEngine(reader.engine);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode !== 'barcode' || cameraError || problem || choices || manualOpen || busy) return;
    const reader = readerRef.current;
    if (!reader || reader.engine === 'none') return;

    scanning.current = true;
    let frame = 0;
    let cancelled = false;

    const tick = async () => {
      if (cancelled || !scanning.current) return;
      const video = videoRef.current;
      if (video) {
        const value = await reader.read(video);
        // A detector can finish after manual entry, a choice, or navigation.
        if (cancelled || !scanning.current) return;
        if (value && isProductBarcode(value)) {
          scanning.current = false;
          await lookup(value);
          return;
        }
      }
      // Раньше здесь стояла пауза 300–500 мс, и она же была главным тормозом:
      // сам проход занимает 30–90 мс, а ждали мы впятеро дольше. Теперь следующий
      // кадр берём почти сразу — паузы хватает, чтобы не занять поток целиком.
      frame = window.setTimeout(() => void tick(), 60);
    };

    void tick();
    return () => {
      cancelled = true;
      scanning.current = false;
      window.clearTimeout(frame);
    };
  }, [mode, cameraError, problem, choices, manualOpen, busy, lookup, engine]);

  const labelFileRef = useRef<HTMLInputElement>(null);
  const captureLabel = async (selected?: File) => {
    const video = videoRef.current;
    if (!selected && (!video || video.readyState < 2)) {
      toast('Камера ещё не готова');
      return;
    }
    if(lookupRef.current.controller)return;
    const generation=++lookupRef.current.generation;
    const controller=new AbortController();
    lookupRef.current.controller=controller;
    setBusy(true);
    try {
      const photo = selected ? await readPhotoFile(selected) : (() => {
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 1600 / Math.max(video!.videoWidth, video!.videoHeight));
      canvas.width = Math.round(video!.videoWidth * scale);
      canvas.height = Math.round(video!.videoHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Не удалось обработать кадр');
      context.drawImage(video!, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      return {base64:dataUrl.split(',')[1] ?? '',mediaType:'image/jpeg' as const};
      })();
      if(controller.signal.aborted || generation !== lookupRef.current.generation)return;
      const result = await request<LabelPhotoResult>('/api/foods/ocr', { method:'POST', body:{ imageBase64: photo.base64, mediaType: photo.mediaType }, signal:controller.signal });
      if(controller.signal.aborted || generation !== lookupRef.current.generation)return;
      const prefill = labelPrefill(result);
      hapticSuccess();
      stop();
      navigate(`/nutrition/food?${query}`, { state: { foodOrigin: origin, prefill } });
    } catch (error) {
      if(controller.signal.aborted || generation !== lookupRef.current.generation)return;
      setProblem({
        kind: 'ocr',
        message:
          error instanceof ApiError
            ? error.message
            : error instanceof Error ? error.message : 'Не удалось распознать этикетку — добавьте вручную',
      });
    } finally {
      if(generation===lookupRef.current.generation){lookupRef.current.controller=null;setBusy(false);}
    }
  };

  const manualNavigate = (code?: string) => {
    invalidateLookup(); stop();
    navigate(`/nutrition/food?${query}`, { state: code ? { foodOrigin: origin, prefill: {
      name: '', brand: null, grams: 100, basis: 'per100', kcal: null,
      protein: null, fat: null, carbs: null, barcode: code, source: 'barcode', barcodeOrigin: 'manual',
    } satisfies ScanPrefill } : { foodOrigin: origin } });
  };

  const close = () => {
    invalidateLookup();
    stop();
    if (origin) navigate(-1);
    else navigate(`/nutrition?date=${date}`, { replace: true });
  };

  return (
    <div className="cam">
      <video ref={videoRef} playsInline muted autoPlay />
      {cameraError && !choices ? (
        <div className="cam-fallback">
          <p>{cameraError}</p>
        </div>
      ) : null}

      {!choices ? <div className="frame" aria-hidden="true">
        <s />
        <s />
        <s />
        <s />
        {mode === 'barcode' ? <em /> : null}
      </div> : null}

      <div className="cam-top">
        <button className="round" type="button" onClick={close} aria-label="Закрыть">
          <CloseIcon />
        </button>
        {!choices ? <button className="round" type="button" aria-label="Вспышка" disabled>
          <FlashIcon />
        </button> : null}
      </div>

      {!choices ? <p className="cam-hint">
        {mode === 'barcode' ? 'Наведите камеру на штрихкод' : 'Наведите камеру на таблицу КБЖУ'}
        <small>
          {mode !== 'barcode'
            ? 'Снимок уйдёт на распознавание'
            : engine === 'loading'
              ? 'Готовим распознавание…'
              : engine === 'none'
                ? 'Автораспознавание недоступно — введите цифры'
                : 'Распознаем автоматически'}
        </small>
      </p> : null}

      <div className={`cam-sheet${choices ? ' cam-sheet-choices' : ''}`}>
        <div className="grab" />
        {choices ? <section className="cam-choices" aria-label="Совпадения магазинного кода">
          <header>
            <h2 ref={choiceHeadingRef} tabIndex={-1}>Выберите продукт</h2>
            <p className="nt">С этим кодом сохранено несколько продуктов. Выберите свой по упаковке.</p>
          </header>
          <div className="cam-choice-list">
            {choices.foods.map(food => <button className="cam-choice-row" type="button" key={food.id}
              onClick={() => handleFound(food, true, choices.code)}>
              <span className="cam-choice-name">{food.name}</span>
              {food.brand ? <span className="cam-choice-brand">{food.brand}</span> : null}
              <span className="cam-choice-nutrition">
                {food.kcal100 ?? '—'} ккал
                {food.nutritionBasis === 'per100ml' ? ' / 100 мл' : food.nutritionBasis === 'per100g' ? ' / 100 г' : ''}
                <span>Б {food.p100 ?? '—'} · Ж {food.f100 ?? '—'} · У {food.c100 ?? '—'}</span>
              </span>
            </button>)}
          </div>
          <div className="cam-choice-actions">
            <button className="b" type="button" onClick={backToScan}>Вернуться к сканированию</button>
            <button className="lnk" type="button" onClick={() => manualNavigate(choices.code)}>Добавить другой продукт вручную <span aria-hidden="true">›</span></button>
          </div>
        </section> : <>
        <div className="seg full" role="group" aria-label="Что сканируем">
          <button
            type="button"
            className={mode === 'barcode' ? 'on' : undefined}
            aria-pressed={mode === 'barcode'}
            onClick={() => { invalidateLookup(); setBusy(false); setMode('barcode'); }}
          >
            Штрихкод
          </button>
          <button
            type="button"
            className={mode === 'label' ? 'on' : undefined}
            aria-pressed={mode === 'label'}
            onClick={() => { invalidateLookup(); setBusy(false); setMode('label'); }}
          >
            Этикетка с КБЖУ
          </button>
        </div>

        {mode === 'label' ? (
          <><button className="b wide" type="button" disabled={busy} onClick={() => labelFileRef.current?.click()}>Галерея</button>
          <input ref={labelFileRef} type="file" accept={PHOTO_ACCEPT} aria-label="Фото этикетки из галереи" hidden onChange={e => { const selected=e.target.files?.[0]; e.target.value=''; if(selected)void captureLabel(selected); }} />
          <button
            className="b p big"
            type="button"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => void captureLabel()}
            disabled={busy || Boolean(cameraError)}
          >
            {busy ? 'Распознаём…' : 'Сделать снимок'}
          </button></>
        ) : (
          <div style={{ marginTop: 8 }}>
            {manualOpen ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0' }}>
                <input
                  className="f"
                  style={{ marginTop: 0 }}
                  inputMode="numeric"
                  placeholder="4600000000000"
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value)}
                  autoFocus
                />
                <button
                  className="b p"
                  type="button"
                  disabled={manualCode.length < 8 || busy}
                  onClick={() => void lookup(manualCode)}
                >
                  Найти
                </button>
              </div>
            ) : (
              <button ref={manualToggleRef} className="lnk" type="button" onClick={() => setManualOpen(true)}>
                Ввести цифры штрихкода <span className="mu">›</span>
              </button>
            )}
            <button
              className="lnk"
              type="button"
              onClick={() => {
                manualNavigate(problem?.code || manualCode || lookupRef.current.code);
              }}
            >
              Добавить вручную <span className="mu">›</span>
            </button>
          </div>
        )}
        </>}
      </div>

      {problem ? (
        <div className="overlay" onClick={() => setProblem(null)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grab" />
            <h2>
              {problem.kind === 'not_found' ? 'Не нашли продукт' : 'Каталог недоступен'}
            </h2>
            <p className="nt" style={{ margin: '6px 0 16px' }}>
              {problem.code && problem.kind !== 'not_found' ? (
                <>
                  Код <span className="code">{problem.code}</span>{' '}
                </>
              ) : null}
              {problem.message}
              {problem.kind === 'not_found'
                ? '. Добавьте продукт вручную — мы запомним его для следующего раза.'
                : ''}
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              <button
                className="b p"
                type="button"
                onClick={() => {
                  manualNavigate(problem.code);
                }}
              >
                Добавить вручную
              </button>
              <button
                className="b"
                type="button"
                onClick={() => {
                  setProblem(null);
                  setManualCode('');
                  lookupRef.current.code = '';
                }}
              >
                Сканировать ещё раз
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
